import { isEqual } from 'lodash';
import * as Yup from 'yup';

import type { FieldGroup, HierarchySubField } from '~/components/types/incident-configuration-types';
import type { HierarchyTree, HierarchyTreeHierarchyNode } from '~/server_shared/openapi/data-contracts';

export interface HierarchyIndexEntry {
  node: HierarchyTreeHierarchyNode;
  parentId: string | null;
  depth: number;
}

export type HierarchyIndex = Map<string, HierarchyIndexEntry>;

/** One level's worth of stored state: the id that was chosen, its label, and whether the tree still carries it. */
export interface LevelSelection {
  id: string;
  desc: string;
  isStale: boolean;
}

const EMPTY_SELECTION: LevelSelection = { id: '', desc: '', isStale: false };

/**
 * Flatten the tree into `id -> {node, parentId, depth}`.
 *
 * A flat map is sound because the contract guarantees node ids are globally unique and
 * path-qualified across the whole payload. The walk uses an explicit stack: depth is bounded only
 * by the payload's byte cap, not by the configured sub-field count, so recursion can overflow.
 */
export function indexTree(tree: HierarchyTree): HierarchyIndex {
  const index: HierarchyIndex = new Map();
  // Reversed so the LIFO stack pops in document order, which is what makes the root filter in
  // `optionsForLevel` return the roots in the order the source sent them.
  const stack: HierarchyIndexEntry[] = [...(tree?.nodes ?? [])]
    .reverse()
    .map((node) => ({ node, parentId: null, depth: 0 }));

  while (stack.length > 0) {
    const entry = stack.pop() as HierarchyIndexEntry;
    index.set(entry.node.id, entry);
    const children = entry.node.children ?? [];
    for (let i = children.length - 1; i >= 0; i -= 1) {
      stack.push({ node: children[i], parentId: entry.node.id, depth: entry.depth + 1 });
    }
  }

  return index;
}

/**
 * The options one level offers: the roots for a null parent, else that node's children.
 *
 * An empty list is also how leaf-ness reaches the picker, and the source picks how it spells that:
 * `children` absent, `null` and `[]` all land on the `?? []` below. That is why nothing derives
 * leaf-ness separately -- a level with no options is exactly a level that cannot be reached.
 */
export function optionsForLevel(index: HierarchyIndex, parentId: string | null): HierarchyTreeHierarchyNode[] {
  if (parentId === null) {
    return [...index.values()].filter((entry) => entry.parentId === null).map((entry) => entry.node);
  }

  return index.get(parentId)?.node.children ?? [];
}

export type GroupSelection = Record<string, string>;
export type GroupValues = Record<string, GroupSelection>;

export function groupSelection(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? Object.fromEntries(Object.entries(value))
    : {};
}

const asString = (value: unknown): string | null => (typeof value === 'string' && value !== '' ? value : null);

/** Read scalar IDs from one group and resolve their labels separately. */
export function readStoredSelection(
  values: Record<string, unknown>,
  subFields: HierarchySubField[],
  labels: Record<string, string> = {}
): LevelSelection[] {
  // Both hosts are `.jsx` with `checkJs` off, and `configured_fields_values` is legitimately absent
  // on a claim that has never stored one, so TS's non-optional parameter is not a runtime guarantee.
  const stored = values ?? {};

  return subFields.map((subField) => {
    const id = asString(stored[subField.id]);
    if (id === null) {
      return { ...EMPTY_SELECTION };
    }

    return { id, desc: asString(labels[id]) ?? id, isStale: false };
  });
}

/**
 * Re-point each stored selection at the freshly fetched tree: refresh the label where the id is
 * still there, flag it stale where it is not. A stale selection keeps its ID and cached label.
 * Only a user edit clears it.
 */
export function reconcileSelection(index: HierarchyIndex, stored: LevelSelection[]): LevelSelection[] {
  return stored.map((selection) => {
    if (selection.id === '') {
      return { ...EMPTY_SELECTION };
    }

    const entry = index.get(selection.id);

    return entry
      ? { id: selection.id, desc: entry.node.desc, isStale: false }
      : { id: selection.id, desc: selection.desc, isStale: true };
  });
}

/** Replace the complete group, preserve ancestors, and omit cleared descendants. */
export function writeSelection(
  subFields: HierarchySubField[],
  values: Record<string, unknown>,
  levelIndex: number,
  node: HierarchyTreeHierarchyNode | null
): GroupSelection {
  const selected: GroupSelection = {};
  subFields.slice(0, levelIndex).forEach((subField) => {
    const id = asString(values[subField.id]);
    if (id !== null) selected[subField.id] = id;
  });
  if (node) selected[subFields[levelIndex].id] = node.id;
  return selected;
}

/**
 * The groups one section of a claim surface should render: active, placed in that section, and
 * supported by the claim's sub-type categories. Every host shares this so their filters cannot
 * drift, and it applies the same placement and category conditions as the sibling configured-fields
 * filter: 'incident_details' owns tagged and untagged groups, every other section (supplementary_info
 * and the custom sections) matches its id exactly.
 */
export function activeFieldGroups(
  incidentConfiguration: { incident_details?: { field_groups?: FieldGroup[] | null } } | null | undefined,
  isFieldSupportedByCategories: (fieldGroup: FieldGroup) => boolean,
  section: string
): FieldGroup[] {
  const fieldGroups = incidentConfiguration?.incident_details?.field_groups ?? [];
  const isPlacedInSection = (fieldGroup: FieldGroup) =>
    section === 'incident_details'
      ? fieldGroup.section === 'incident_details' || !fieldGroup.section
      : fieldGroup.section === section;

  return fieldGroups.filter(
    (fieldGroup) =>
      fieldGroup.active !== false && isPlacedInSection(fieldGroup) && isFieldSupportedByCategories(fieldGroup)
  );
}

/** The top-level keys owned by these groups, in configured order. */
export function fieldGroupKeys(fieldGroups: FieldGroup[]): string[] {
  return fieldGroups.map((fieldGroup) => fieldGroup.id);
}

/**
 * The groups a claim-page section save sends: those the submitted values carry whose selection differs
 * from the stored one. A section dialog holds every group it renders, and re-sending an unchanged one
 * can overwrite a newer selection.
 */
export function changedFieldGroupValues(
  fieldGroups: FieldGroup[],
  values: Record<string, unknown> | undefined,
  storedValues: Record<string, unknown> | null | undefined
): Record<string, Record<string, unknown>> {
  const changed: Record<string, Record<string, unknown>> = {};
  fieldGroups.forEach(({ id }) => {
    if (values === undefined || !(id in values)) return;
    const selection = groupSelection(values[id]);
    if (!isEqual(selection, groupSelection(storedValues?.[id]))) changed[id] = selection;
  });
  return changed;
}

/** Unselected groups start as empty objects. */
export function emptyFieldGroupFormikValues(fieldGroups: FieldGroup[]): GroupValues {
  return Object.fromEntries(fieldGroupKeys(fieldGroups).map((key) => [key, {}]));
}

/** Require the first level only: a valid branch can end before the deepest configured level. */
export function fieldGroupValidations(fieldGroups: FieldGroup[]): Record<string, Yup.AnyObjectSchema> {
  return Object.fromEntries(
    fieldGroups
      .filter((fieldGroup) => fieldGroup.mandatory && fieldGroup.active !== false && fieldGroup.sub_fields.length > 0)
      .map((fieldGroup) => [
        fieldGroup.id,
        Yup.object().shape({
          [fieldGroup.sub_fields[0].id]: Yup.string().required('Required'),
        }),
      ])
  );
}
